import { useRef, useState, useCallback, useEffect } from "react";
import "../../../../../css/AML/Inputs/AMLInput1.css";
import "../../../../../css/AML/FileUploadContainer/ImageUploadContainer.css";
import { ReactComponent as UploadIcon } from "../../../../../assets/icons/aml/uploadImage.svg";
import { ReactComponent as CheckIcon } from "../../../../../assets/icons/aml/checkCircleGreen.svg";
import { ReactComponent as CloseIcon } from "../../../../../assets/icons/aml/close.svg";
import { ReactComponent as ErrorIcon } from "../../../../../assets/icons/aml/emergencyIcon.svg";

const ImageUploadContainer = ({
  label = "Front Side",
  onChange, // (file | undefined) => void
  onError, // (message: string) => void
  accept = ["image/jpeg", "image/png", "image/jpg"],
  maxSizeMB = 17,
  maxDimension = 1920,
  className = "",
  id,
  errorMessage = "", // NEW: parent-provided error to display
}) => {
  const inputRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [internalError, setInternalError] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fileName, setFileName] = useState("");

  const acceptAttr = accept.join(",");

  // Cleanup object URL when it changes or on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const readImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const { width, height } = img;
          URL.revokeObjectURL(url);
          resolve({ width, height });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Could not read image."));
        };
        img.src = url;
      } catch (e) {
        reject(new Error("Could not open image."));
      }
    });

  const resizeImage = (file, maxDimension) =>
    new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const { width, height } = img;

          // Check if resizing is needed
          if (width <= maxDimension && height <= maxDimension) {
            URL.revokeObjectURL(url);
            resolve(file); // No resizing needed
            return;
          }

          // Calculate new dimensions while maintaining aspect ratio
          let newWidth = width;
          let newHeight = height;

          if (width > height) {
            if (width > maxDimension) {
              newWidth = maxDimension;
              newHeight = (height * maxDimension) / width;
            }
          } else {
            if (height > maxDimension) {
              newHeight = maxDimension;
              newWidth = (width * maxDimension) / height;
            }
          }

          // Create canvas and resize
          const canvas = document.createElement("canvas");
          canvas.width = newWidth;
          canvas.height = newHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          URL.revokeObjectURL(url);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Could not resize image."));
                return;
              }
              // Create a new File object from the blob
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            },
            file.type,
            0.95, // Quality for JPEG
          );
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Could not read image for resizing."));
        };
        img.src = url;
      } catch (e) {
        reject(new Error("Could not resize image."));
      }
    });

  const validateFile = async (file) => {
    if (!file) return "No file selected.";
    if (!accept.includes(file.type)) return `Upload only JPG, PNG, JPEG files.`;
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) return `File is too large. Max ${maxSizeMB}MB.`;
    return "";
  };

  const handleFiles = useCallback(
    async (files) => {
      const file = files?.[0];
      if (!file) return;

      setInternalError("");
      setIsSuccess(false);

      try {
        // First resize the image if needed
        const resizedFile = await resizeImage(file, maxDimension);

        // Then validate the resized file
        const err = await validateFile(resizedFile);
        if (err) {
          // reset state
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
          setFileName("");
          setIsSuccess(false);
          setInternalError(err);
          onError?.(err);
          onChange?.(undefined);
          return;
        }

        // create preview
        const url = URL.createObjectURL(resizedFile);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(url);
        setFileName(resizedFile.name);
        setIsSuccess(true);
        onChange?.(resizedFile);
      } catch (e) {
        const msg = "Unexpected error while processing the image.";
        setInternalError(msg);
        onError?.(msg);
        onChange?.(undefined);
      }
    },
    [onChange, onError, previewUrl, maxDimension],
  );

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsActive(false);
    await handleFiles(e.dataTransfer?.files);
  };

  const onBrowse = () => inputRef.current?.click();

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onBrowse();
    }
  };

  const reset = () => {
    setIsActive(false);
    setInternalError("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileName("");
    setIsSuccess(false);
    onChange?.(undefined);
  };

  // Merge internal and external errors; external takes precedence
  const combinedError = errorMessage || internalError;

  return (
    <div className={`AML-upload-wrapper ${className}`}>
      <div
        id={id}
        className={`AML-upload-dropzone ${isActive ? "is-active" : ""} ${
          combinedError ? "has-error" : ""
        } ${isSuccess ? "is-success" : ""}`}
        role="button"
        tabIndex={0}
        onClick={onBrowse}
        onKeyDown={onKeyDown}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsActive(false);
        }}
        onDrop={onDrop}
        aria-label="Upload image"
        aria-invalid={!!combinedError}
        aria-describedby={combinedError ? `${id}-error` : undefined}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: "none" }}
        />

        {/* Close (only visible in success) */}

        {/* Icon (upload by default; check in success; error icon on error) */}
        <div aria-hidden="true">
          {isSuccess ? (
            <CheckIcon />
          ) : combinedError ? (
            <ErrorIcon />
          ) : (
            <UploadIcon />
          )}
        </div>

        {/* Title becomes file name on success */}
        <div className="AML-Text-Input" title={isSuccess ? fileName : label}>
          {isSuccess ? fileName : label}
        </div>

        {/* Subtitle becomes success text */}
        <div className="AML-Text-Body">
          {isSuccess ? (
            "Upload Successful"
          ) : (
            <>
              Drag & drop or{" "}
              <span
                className="AML-upload-browse"
                onClick={(e) => {
                  e.stopPropagation();
                  onBrowse();
                }}
              >
                browse
              </span>
            </>
          )}
        </div>

        {/* Optional: preview thumbnail (hidden by CSS if you don’t want it) */}
        {/* {previewUrl && (
          <img
            src={previewUrl}
            alt="Preview"
            className="AML-upload-preview"
            onError={() => {
              const msg = "Preview failed to load.";
              setInternalError(msg);
              onError?.(msg);
            }}
          />
        )} */}

        {/* Hint (hide via CSS in success state) */}
        <div className="AML-upload-hint">
          {combinedError
            ? combinedError
            : `JPG, PNG | Max ${maxSizeMB}MB | Max ${maxDimension}px`}
        </div>
        <CloseIcon
          className="AML-upload-close"
          onClick={(e) => {
            e.stopPropagation();
            reset();
          }}
          aria-label="Remove selected image"
        />
      </div>

      {/* {combinedError && (
        <div id={`${id}-error`} className="AML-upload-error" aria-live="polite">
          {combinedError}
        </div>
      )} */}
    </div>
  );
};

export default ImageUploadContainer;
